/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903001650 — SelectProduct */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import {
    API,
    DEVICE_STATUS,
    IS_DEV,
    ORDER_STATUS,
} from '../../../../shared/constants';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { useTimer } from '../../../../shared/hooks/useTimer';
import { FeedbackAndReceipt } from '../../../../shared/layouts/FeedbackAndReceipt/FeedbackAndReceipt';
import { apiClient } from '../../../../shared/lib/apiClient';
import { OrderDto, toOrder } from '../../../order/lib/to-order';
import { getSelectScreenDevUrl } from '../../../../shared/lib/dev';
import {
    debug,
    getVmDetails,
    screenLog,
} from '../../../../shared/lib/general';
import { EVENT_NAMES, useSendEventLog, WaitSource } from '../../../event-log';
import { ContentBox, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { AnimatedIcon } from '../../../../shared/ui/AnimatedIcon/AnimatedIcon';
import foodChoiceAnimation from '../../../../shared/ui/AnimatedIcon/animations/food-choice.json';
import { useOrderActions } from '../../../order/hooks/useOrderActions';
import { withOrderHydration } from '../../../order/hooks/withOrderHydration';
import { ORDER_ROUTES } from '../../../order/order.constants';
import { PAYMENT_ROUTES } from '../../payment.constants';

import styles from './SelectProduct.module.css';

// Logic of this screen
// 1. In the loop till timer is not 0 we check if product is selected
// 2. If product is selected in the responce device_status is 3
// 3. If device_status is 3 we go to the next screen

const SelectProductBody = () => {
    const { t } = useTranslation();
    const { onTimerTimeoutHandler } = useOrderActions();
    const { sendEvent, sendTimeout } = useSendEventLog();
    const navigate = useNavigate();
    const vm = useAppStore((s) => s.vm);
    const { isCT5, selectTimeoutSec } = getVmDetails(vm);
    const timerHook = useTimer(selectTimeoutSec);

    const order = useAppStore((s) => s.order);
    const setOrder = useAppStore((s) => s.setOrder);
    const [isProductSelected, setIsProductSelected] = useState<boolean>(false);


    useEffect(() => {
        screenLog('Select product');
        timerHook.start();

        return () => {
            timerHook.reset();
        };
    }, []);

    useEffect(() => {
        // wait for user selection
        if (timerHook.time <= 0) {
            sendTimeout({ name: EVENT_NAMES.timeout['select-product'] });
            onTimerTimeoutHandler('select');
        } else {
            if (!order.id) {
                debug('order id is not set on selection screen', 'error');
                navigate(generatePath(ORDER_ROUTES.error, { id: 'selection' }), {
                    replace: true,
                });
            } else if (!isProductSelected) {
                (async () => {
                    try {
                        const url = IS_DEV
                            ? getSelectScreenDevUrl(timerHook.time)
                            : `${API.ORDER}?id=${order.id}&t=${timerHook.time}`;
                        const { data: dto } = await apiClient.get<OrderDto>(url);
                        const orderData = toOrder(dto);
                        debug('order data fetched', 'data', orderData);

                        if (
                            orderData.device_status ===
                            DEVICE_STATUS.PRODUCT_SELECTED
                        ) {
                            sendEvent({ name: EVENT_NAMES.static.PRODUCT_SELECTED });
                            setOrder(orderData);
                            setIsProductSelected(true);

                            const isWait = isCT5;
                            const route = isWait
                                ? PAYMENT_ROUTES.wait
                                : PAYMENT_ROUTES.payment;

                            setTimeout(() => {
                                navigate(
                                    generatePath(route, {
                                        id: String(orderData.id),
                                    }),
                                    {
                                        replace: true,
                                        state: isWait
                                            ? { waitSource: 'product' satisfies WaitSource }
                                            : undefined,
                                    },
                                );
                            }, 500);
                        }

                        // rejection from device
                        if (
                            orderData.device_status ===
                                DEVICE_STATUS.COMPLETED &&
                            (orderData.status === ORDER_STATUS.REFUNDED ||
                                orderData.status ===
                                    ORDER_STATUS.REFUND_DEFFERED)
                        ) {
                            sendEvent({ name: EVENT_NAMES.static.PRODUCT_REJECTED_BY_DEVICE });
                            navigate(
                                generatePath(ORDER_ROUTES.refund, {
                                    id: String(orderData.id),
                                }),
                                { replace: true },
                            );
                        }
                    } catch (error) {
                        debug('some error during fetching order data', 'error');
                        navigate(
                            generatePath(ORDER_ROUTES.error, {
                                id: String(order.id ?? 'order'),
                            }),
                            { replace: true },
                        );
                    }
                })();
            }
        }
    }, [timerHook.time]);

    return (
        <ScreenWrapper>
            <ScreenTitle>{t('Select product')}</ScreenTitle>
            <ContentBox className={styles.animationWrapper}>
                <AnimatedIcon className={styles.animation} animationData={foodChoiceAnimation} />
                <FeedbackAndReceipt screenName="select" />
            </ContentBox>
        </ScreenWrapper>
    );
};

const SelectProduct = withOrderHydration(SelectProductBody);

export { SelectProduct };
